import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Button,
  CheckIcon,
  CloseIcon,
  Paragraph,
} from '@quickmd-llc/design-system';
import {
  CreditCardForm,
  FormValues,
} from '@components/forms/CreditCardForm/CreditCardForm';
import {
  CardErrorsByField,
  emptyCardErrors,
} from '@components/forms/CreditCardForm/utils';
import { PatientCreditCard } from '@types';
import { addCard } from './utils';
import { useLocalAdyen } from '@components/forms/CreditCardForm/hooks/useLocalAdyen';

interface Props {
  handleClose: () => void;
  setSelectedCreditCard: (creditCard: PatientCreditCard) => void;
}

const defaultValues = {
  creditCardId: undefined,
  creditCard: { cardCode: '', cardExpiration: '', cardNumber: '' },
};

export default function AddCardPopUp({
  handleClose,
  setSelectedCreditCard,
}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formMethods = useForm<FormValues>({ mode: 'onBlur', defaultValues });

  const {
    getValues,
    formState: { isValid },
    watch,
  } = formMethods;
  const sameAsHomeAddress = watch('sameAsHomeAddress');
  const address = watch('address');

  const isAddressInvalid =
    !sameAsHomeAddress &&
    (!address?.streetAddress1 || !address?.city || !address?.zip);

  const [cardErrors, setCardErrors] =
    useState<CardErrorsByField>(emptyCardErrors);

  const { adyen } = useLocalAdyen();

  async function handleOnSubmit() {
    try {
      const data = getValues();

      const adyenEncryptedMetadata = await adyen?.getAdyenEncryptedData();
      if (!adyenEncryptedMetadata) {
        return;
      }

      setIsLoading(true);
      setCardErrors(emptyCardErrors);

      await addCard({
        data,
        setCard: setSelectedCreditCard,
        closeAddCard: handleClose,
        setCardErrors,
        adyenEncryptedMetadata,
      });

      setIsLoading(false);
    } finally {
      adyen?.unlockFormFieldsOnPaymentError();
    }
  }
  return (
    <FormProvider {...formMethods}>
      <div className="flex max-h-max max-w-lg flex-1 flex-col self-center rounded-lg bg-white p-6">
        <div className="mb-2 flex flex-row items-center justify-between">
          <Paragraph className="text-base font-semibold lg:text-2xl">
            New Card
          </Paragraph>
          <CloseIcon className="cursor-pointer" onClick={handleClose} />
        </div>
        <form className="flex flex-1 flex-col gap-4">
          <CreditCardForm
            cardErrors={cardErrors}
            hideTitle
            AdyenCardForm={adyen?.AdyenCardForm}
          />

          <Button
            className="flex w-full self-end lg:w-max"
            disabled={!isValid || isAddressInvalid}
            loading={isLoading}
            onClick={handleOnSubmit}
          >
            <CheckIcon /> Save Card
          </Button>
        </form>
      </div>
    </FormProvider>
  );
}
